// Signage → Reglas de realce (/signage/reglas). Etiquetas promocionales que el
// reproductor pinta sobre los productos según condición: siempre, stock bajo o
// franja horaria (opc. acotadas a una categoría). El precio final por local ya
// sale solo (motor de descuentos del POS); esto agrega el "gancho" visual.
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Tag, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { Button } from '@/components/ui/Button';
import { Campo, Modal, Switch, fieldCls } from '@/features/pos/shared';
import { sigInsert, sigList, sigSoftDelete, sigUpdate } from './shared';

interface Regla {
  id: string; nombre: string; condicion: 'siempre' | 'stock_bajo' | 'franja';
  umbral: number | null; hora_inicio: string | null; hora_fin: string | null;
  categoria_id: string | null; etiqueta: string; color: string; prioridad: number; activa: boolean;
}
interface Cat { id: string; nombre: string }
const COND = [{ v: 'siempre', l: 'Siempre' }, { v: 'stock_bajo', l: 'Stock bajo' }, { v: 'franja', l: 'Franja horaria' }];

export function Reglas() {
  const qc = useQueryClient();
  const [edit, setEdit] = useState<Partial<Regla> | null>(null);
  const [guardando, setGuardando] = useState(false);

  const { data: reglas = [] } = useQuery<Regla[]>({ queryKey: ['sig-reglas'], queryFn: () => sigList<Regla>('regla?select=*&deleted_at=is.null&order=prioridad.desc') });
  const { data: cats = [] } = useQuery<Cat[]>({
    queryKey: ['inv-categorias'],
    queryFn: () => apiClient.get<Cat[]>('/rest/inventario.categorias?select=id,nombre&deleted_at=is.null&order=nombre').then((r) => r.data),
  });
  const catNom = (id: string | null) => (id ? cats.find((c) => c.id === id)?.nombre ?? '—' : 'Todas');
  const invalidar = () => qc.invalidateQueries({ queryKey: ['sig-reglas'] });

  const guardar = async () => {
    if (!edit?.nombre || !edit.etiqueta) { toast.error('Nombre y etiqueta son obligatorios'); return; }
    setGuardando(true);
    try {
      const body = {
        nombre: edit.nombre, etiqueta: edit.etiqueta, color: edit.color || '#c6303a',
        condicion: edit.condicion || 'siempre', umbral: edit.umbral ?? 5,
        hora_inicio: edit.hora_inicio || '00:00', hora_fin: edit.hora_fin || '23:59',
        categoria_id: edit.categoria_id || null, prioridad: edit.prioridad ?? 10, activa: edit.activa ?? true,
      };
      if (edit.id) await sigUpdate('regla', edit.id, body); else await sigInsert('regla', body);
      setEdit(null); invalidar(); toast.success('Regla guardada');
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setGuardando(false); }
  };

  const detalle = (r: Regla) => r.condicion === 'stock_bajo' ? `stock ≤ ${r.umbral}`
    : r.condicion === 'franja' ? `${(r.hora_inicio || '').slice(0, 5)}–${(r.hora_fin || '').slice(0, 5)}` : 'siempre';

  return (
    <div className="p-6 space-y-4 max-w-4xl">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Tag className="w-5 h-5" /> Reglas de realce</h1>
        <Button className="ml-auto" onClick={() => setEdit({ condicion: 'siempre', umbral: 3, color: '#c6303a', prioridad: 10, activa: true })}>
          <Plus className="w-4 h-4 mr-1" /> Nueva regla
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">La etiqueta se pinta sobre el producto en el reproductor cuando se cumple la condición. El <strong>precio ya sale con el descuento del local</strong> automáticamente; esto es el gancho visual (OFERTA, HORA FELIZ, etc.). Para que el happy hour tenga <strong>precio real</strong> en esa franja, ponle hora/días al Promo % del producto (Inventario → Productos → Descuentos) o a la promoción del POS — la caja y la pantalla cambian juntas.</p>

      <div className="border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-muted-foreground border-b bg-muted/30">
            <th className="py-2 px-3 text-right w-10">N°</th><th className="py-2 px-3 text-left">Regla</th>
            <th className="py-2 px-3 text-left">Etiqueta</th><th className="py-2 px-3 text-left">Condición</th>
            <th className="py-2 px-3 text-left">Categoría</th><th className="py-2 px-3 text-right">Prior.</th>
            <th className="py-2 px-3 text-center">Activa</th><th className="py-2 px-3" />
          </tr></thead>
          <tbody>
            {reglas.map((r, i) => (
              <tr key={r.id} className="border-b last:border-0">
                <td className="py-2 px-3 text-right text-muted-foreground">{i + 1}</td>
                <td className="py-2 px-3">{r.nombre}</td>
                <td className="py-2 px-3"><span className="text-xs font-bold uppercase text-white px-2 py-0.5 rounded" style={{ background: r.color }}>{r.etiqueta}</span></td>
                <td className="py-2 px-3 text-muted-foreground">{COND.find((c) => c.v === r.condicion)?.l}: {detalle(r)}</td>
                <td className="py-2 px-3 text-muted-foreground">{catNom(r.categoria_id)}</td>
                <td className="py-2 px-3 text-right">{r.prioridad}</td>
                <td className="py-2 px-3 text-center">{r.activa ? 'Sí' : 'No'}</td>
                <td className="py-2 px-3 text-right">
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => setEdit(r)}><Pencil className="w-4 h-4" /></button>
                  <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Eliminar" onClick={() => sigSoftDelete('regla', r.id).then(invalidar)}><Trash2 className="w-4 h-4 text-red-500" /></button>
                </td>
              </tr>
            ))}
            {!reglas.length && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Sin reglas. Crea una (p. ej. "OFERTA" cuando el stock baje de 3).</td></tr>}
          </tbody>
        </table>
      </div>

      {edit && (
        <Modal title={edit.id ? 'Editar regla' : 'Nueva regla'} onClose={() => setEdit(null)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Nombre"><input className={fieldCls} value={edit.nombre ?? ''} onChange={(e) => setEdit({ ...edit, nombre: e.target.value })} autoFocus /></Campo>
              <Campo label="Etiqueta (badge)"><input className={fieldCls} maxLength={30} placeholder="OFERTA" value={edit.etiqueta ?? ''} onChange={(e) => setEdit({ ...edit, etiqueta: e.target.value })} /></Campo>
              <Campo label="Condición">
                <select className={fieldCls} value={edit.condicion ?? 'siempre'} onChange={(e) => setEdit({ ...edit, condicion: e.target.value as Regla['condicion'] })}>
                  {COND.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
                </select>
              </Campo>
              <Campo label="Color">
                <input type="color" value={edit.color ?? '#c6303a'} onChange={(e) => setEdit({ ...edit, color: e.target.value })} className="w-10 h-9 rounded border" />
              </Campo>
              {edit.condicion === 'stock_bajo' && (
                <Campo label="Stock ≤"><input className={fieldCls} type="number" min={1} value={edit.umbral ?? 3} onChange={(e) => setEdit({ ...edit, umbral: Number(e.target.value) })} /></Campo>
              )}
              {edit.condicion === 'franja' && (
                <>
                  <Campo label="Desde"><input className={fieldCls} type="time" value={(edit.hora_inicio || '17:00').slice(0, 5)} onChange={(e) => setEdit({ ...edit, hora_inicio: e.target.value })} /></Campo>
                  <Campo label="Hasta"><input className={fieldCls} type="time" value={(edit.hora_fin || '19:00').slice(0, 5)} onChange={(e) => setEdit({ ...edit, hora_fin: e.target.value })} /></Campo>
                </>
              )}
              <Campo label="Categoría (opcional)">
                <select className={fieldCls} value={edit.categoria_id ?? ''} onChange={(e) => setEdit({ ...edit, categoria_id: e.target.value })}>
                  <option value="">Todas</option>
                  {cats.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </Campo>
              <Campo label="Prioridad"><input className={fieldCls} type="number" value={edit.prioridad ?? 10} onChange={(e) => setEdit({ ...edit, prioridad: Number(e.target.value) })} /></Campo>
            </div>
            <Switch checked={edit.activa ?? true} onChange={(v) => setEdit({ ...edit, activa: v })} label="Regla activa" />
            <div className="flex justify-end gap-2 pt-2"><Button variant="ghost" onClick={() => setEdit(null)}>Cancelar</Button><Button onClick={() => void guardar()} isLoading={guardando}>Guardar</Button></div>
          </div>
        </Modal>
      )}
    </div>
  );
}
